/**
 * CategoryPropertiesEditor Component
 * 
 * Property editor for Category block type.
 * Allows selecting a category and configuring display options.
 */

import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CategoryBlockProps } from '@/types/menuBuilder';
import { useBranch } from '@/contexts/BranchContext';
import { useCachedCategories } from '@/hooks/useCache';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

interface CategoryPropertiesEditorProps {
  props: CategoryBlockProps;
  onUpdate: (props: Partial<CategoryBlockProps>) => void;
}

export const CategoryPropertiesEditor = memo(function CategoryPropertiesEditor({
  props,
  onUpdate,
}: CategoryPropertiesEditorProps) {
  const { t } = useTranslation();
  const { selectedBranchId } = useBranch();

  // Fetch categories for this branch
  const { data: categoriesData, loading } = useCachedCategories(
    selectedBranchId || ''
  );
  
  const categories = categoriesData || [];

  return (
    <div className="space-y-6">
      {/* Category Selection */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.selectCategory', 'Select Category')}</Label>
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <Select
            value={props.categoryId || ''}
            onValueChange={(value) => onUpdate({ categoryId: value })}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={t(
                  'menuBuilder.chooseCategory',
                  'Choose a category...'
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Layout Mode */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.layoutMode', 'Layout Mode')}</Label>
        <Select
          value={props.layout}
          onValueChange={(value) =>
            onUpdate({ layout: value as CategoryBlockProps['layout'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="grid">{t('menuBuilder.grid', 'Grid')}</SelectItem>
            <SelectItem value="list">{t('menuBuilder.list', 'List')}</SelectItem>
            <SelectItem value="carousel">
              {t('menuBuilder.carousel', 'Carousel')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Item Shape */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.itemShape', 'Item Shape')}</Label>
        <Select
          value={props.itemShape || 'rounded'}
          onValueChange={(value) =>
            onUpdate({ itemShape: value as CategoryBlockProps['itemShape'] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rounded">{t('menuBuilder.shapes.rounded', 'Rounded')}</SelectItem>
            <SelectItem value="square">{t('menuBuilder.shapes.square', 'Square')}</SelectItem>
            <SelectItem value="pill">{t('menuBuilder.shapes.pill', 'Pill')}</SelectItem>
            <SelectItem value="leaf">{t('menuBuilder.shapes.leaf', 'Leaf')}</SelectItem>
            <SelectItem value="arch">{t('menuBuilder.shapes.arch', 'Arch')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Columns (only for grid mode) */}
      {props.layout === 'grid' && (
        <div className="space-y-2">
          <Label>{t('menuBuilder.columns', 'Columns')}</Label>
          <Select
            value={String(props.columns || 2)}
            onValueChange={(value) => onUpdate({ columns: Number(value) as 1 | 2 | 3 })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2">2</SelectItem>
              <SelectItem value="3">3</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Max Items */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.maxItems', 'Max Items (0 = all)')}</Label>
        <Input
          type="number"
          min={0}
          value={props.maxItems || 0}
          onChange={(e) => onUpdate({ maxItems: Number(e.target.value) || undefined })}
        />
      </div>

      {/* Display Options */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium">
          {t('menuBuilder.showOptions', 'Show Options')}
        </h4>

        <div className="flex items-center justify-between">
          <Label htmlFor="showTitle">
            {t('menuBuilder.categoryTitle', 'Category Title')}
          </Label>
          <Switch
            id="showTitle"
            checked={props.showTitle}
            onCheckedChange={(checked) => onUpdate({ showTitle: checked })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="showImages">
            {t('menuBuilder.images', 'Images')}
          </Label>
          <Switch
            id="showImages"
            checked={props.showImages}
            onCheckedChange={(checked) => onUpdate({ showImages: checked })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="showPrices">
            {t('menuBuilder.prices', 'Prices')}
          </Label>
          <Switch
            id="showPrices"
            checked={props.showPrices}
            onCheckedChange={(checked) => onUpdate({ showPrices: checked })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="showDescriptions">
            {t('menuBuilder.descriptions', 'Descriptions')}
          </Label>
          <Switch
            id="showDescriptions"
            checked={props.showDescriptions}
            onCheckedChange={(checked) => onUpdate({ showDescriptions: checked })}
          />
        </div>
      </div>

      {/* Title Styling */}
      {props.showTitle && (
        <div className="space-y-4 border-t pt-4">
          <h4 className="text-sm font-medium">
            {t('menuBuilder.titleStyling', 'Title Styling')}
          </h4>

          {/* Font Size */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.fontSize', 'Font Size')}</Label>
            <Select
              value={props.titleFontSize || 'md'}
              onValueChange={(value) => 
                onUpdate({ titleFontSize: value as CategoryBlockProps['titleFontSize'] })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">{t('menuBuilder.sizes.sm', 'Small')}</SelectItem>
                <SelectItem value="md">{t('menuBuilder.sizes.md', 'Medium')}</SelectItem>
                <SelectItem value="lg">{t('menuBuilder.sizes.lg', 'Large')}</SelectItem>
                <SelectItem value="xl">{t('menuBuilder.sizes.xl', 'Extra Large')}</SelectItem>
                <SelectItem value="2xl">{t('menuBuilder.sizes.2xl', '2x Large')}</SelectItem>
                <SelectItem value="custom">{t('menuBuilder.sizes.custom', 'Custom')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Font Size */}
          {props.titleFontSize === 'custom' && (
            <div className="space-y-2">
              <Label>{t('menuBuilder.customSizePx', 'Custom Size (px)')}</Label>
              <Input
                type="number"
                value={props.customTitleFontSize || 24}
                onChange={(e) => onUpdate({ customTitleFontSize: Number(e.target.value) })}
              />
            </div>
          )}

          {/* Text Color */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.textColor', 'Text Color')}</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={props.titleColor || '#000000'}
                onChange={(e) => onUpdate({ titleColor: e.target.value })}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={props.titleColor || '#000000'}
                onChange={(e) => onUpdate({ titleColor: e.target.value })}
                placeholder="#000000"
                className="flex-1"
              />
            </div>
          </div>
          
          {/* Alignment */}
          <div className="space-y-2">
            <Label>{t('menuBuilder.alignment', 'Alignment')}</Label>
             <Select
              value={props.titleAlignment || 'left'}
              onValueChange={(value) => 
                onUpdate({ titleAlignment: value as CategoryBlockProps['titleAlignment'] })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">{t('menuBuilder.align.left', 'Left')}</SelectItem>
                <SelectItem value="center">{t('menuBuilder.align.center', 'Center')}</SelectItem>
                <SelectItem value="right">{t('menuBuilder.align.right', 'Right')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Item Text Colors */}
      <div className="space-y-4 border-t pt-4">
        <h4 className="text-sm font-medium">
          {t('menuBuilder.itemTextColors', 'Item Text Colors')}
        </h4>

        {/* Item Name Color */}
        <div className="space-y-2">
          <Label>{t('menuBuilder.itemNameColor', 'Item Name Color')}</Label>
          <div className="flex gap-2">
            <Input
              type="color"
              value={props.itemNameColor || '#000000'}
              onChange={(e) => onUpdate({ itemNameColor: e.target.value })}
              className="w-12 h-10 p-1 cursor-pointer"
            />
            <Input
              type="text"
              value={props.itemNameColor || ''}
              onChange={(e) => onUpdate({ itemNameColor: e.target.value })}
              placeholder={t('menuBuilder.default', 'Default')}
              className="flex-1"
            />
          </div>
        </div>

        {/* Item Description Color */}
        {props.showDescriptions && (
          <div className="space-y-2">
            <Label>{t('menuBuilder.itemDescriptionColor', 'Description Color')}</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={props.itemDescriptionColor || '#6b7280'}
                onChange={(e) => onUpdate({ itemDescriptionColor: e.target.value })}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={props.itemDescriptionColor || ''}
                onChange={(e) => onUpdate({ itemDescriptionColor: e.target.value })}
                placeholder={t('menuBuilder.default', 'Default')}
                className="flex-1"
              />
            </div>
          </div>
        )}

        {/* Item Price Color */}
        {props.showPrices && (
          <div className="space-y-2">
            <Label>{t('menuBuilder.itemPriceColor', 'Price Color')}</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={props.itemPriceColor || '#000000'}
                onChange={(e) => onUpdate({ itemPriceColor: e.target.value })}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={props.itemPriceColor || ''}
                onChange={(e) => onUpdate({ itemPriceColor: e.target.value })}
                placeholder={t('menuBuilder.default', 'Default')}
                className="flex-1"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default CategoryPropertiesEditor;
